import {Component, computed, inject, input, model, signal, viewChild, ViewEncapsulation} from '@angular/core';
import {DxPopupModule} from 'devextreme-angular';
import {SharedModule} from '../../../../shared/shared.module';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {PlcProduitComplementaireDTO} from '../../../../core/dtos/plc-produit-complementaire-dto';
import {ProduitDeclinaisonDTO} from '../../../../core/dtos/produit-declinaison-dto';
import {
  PeriodeConditionnementDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {JourToggle} from '../../../../core/models/gestion-produits/declinaison/complementaire/plc/jour-toggle';
import {
  PlcProduitComplementaireChanges
} from '../../../../core/models/gestion-produits/declinaison/complementaire/plc/plc-produit-complementaire-changes';
import {
  SelectionPlcProduitComplementaire
} from '../../../../core/models/gestion-produits/declinaison/complementaire/produit/selection-plc-produit-complementaire';
import {
  ProduitProduitComplementaireService
} from '../../../../core/services/entities/produit-produit-complementaire.service';
import {
  ProduitComplementaireParametrageService
} from '../../../../core/services/entities/produit-complementaire-parametrage.service';
import {ProduitDeclinaisonService} from '../../../../core/services/entities/produit-declinaison.service';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY} from '../../../../core/constants';
import {
  ProduitComplementaireGridComponent
} from '../../../../shared/produits-complementaires/produit-complementaire-grid/produit-complementaire-grid.component';
import {
  DialogPeriodeConditionnementComponent
} from '../../../../shared/produits-complementaires/dialog-periode-conditionnement/dialog-periode-conditionnement.component';
import {DialogSelectionPlcComponent} from '../dialog-selection-plc/dialog-selection-plc.component';

/**
 * Composition d'une proposition d'associations depuis la fiche produit : un produit décliné complémentaire, un ou
 * plusieurs points de livraison, puis le paramétrage de chaque ligne avant enregistrement.
 * La proposition peut couvrir plusieurs produits déclinés : elle s'accumule au fil des sélections.
 */
@Component({
  selector: 'yo-dialog-ajout-association',
  templateUrl: './dialog-ajout-association.component.html',
  styleUrls: ['./dialog-ajout-association.component.scss'],
  standalone: true,
  imports: [DxPopupModule, SharedModule, ProduitComplementaireGridComponent,
            DialogSelectionPlcComponent, DialogPeriodeConditionnementComponent],
  encapsulation: ViewEncapsulation.None
})
export class DialogAjoutAssociationComponent {

  readonly visible = model<boolean>(false);

  /** Produits déclinés complémentaires du produit : le choix de la ligne à associer se fait parmi eux. */
  readonly produitDeclinaisonList = input<ProduitDeclinaisonDTO[]>([]);

  readonly ButtonThemeEnum = ButtonThemeEnum;

  /** Clé composite : la proposition peut porter le même produit décliné sur plusieurs points de livraison. */
  readonly KEY_EXPR = ['plcId', 'produitDeclinaisonId'];

  private readonly produitComplementaireSvc = inject(ProduitProduitComplementaireService);
  private readonly toastSvc = inject(ToastService);
  private readonly paramSvc = inject(ProduitComplementaireParametrageService);

  private readonly grid = viewChild(ProduitComplementaireGridComponent);

  readonly propositionList = signal<PlcProduitComplementaireDTO[]>([]);

  readonly produitDeclinaisonId = signal<number>(null);

  readonly produitDeclinaison = computed(() =>
    this.produitDeclinaisonList().find(declinaison => declinaison.id === this.produitDeclinaisonId()));

  readonly produitDeclinaisonLibelle = computed(() =>
    ProduitDeclinaisonService.buildLibelle(this.produitDeclinaison()));

  /** Points de livraison déjà proposés pour le produit décliné courant : cochés à l'ouverture de la sélection. */
  readonly selectedPlcIdList = computed(() => this.propositionList()
                                                 .filter(ligne => ligne.produitDeclinaisonId === this.produitDeclinaisonId())
                                                 .map(ligne => ligne.plcId));

  readonly selectionVisible = signal<boolean>(false);

  readonly dialogPeriodeVisible = signal<boolean>(false);

  readonly produitPeriode = signal<PlcProduitComplementaireDTO>(null);

  onShowing = (): void => {
    this.propositionList.set([]);
    this.produitDeclinaisonId.set(this.produitDeclinaisonList()[0]?.id ?? null);
  };

  getProduitDeclinaisonLibelle = (declinaison: ProduitDeclinaisonDTO): string =>
    ProduitDeclinaisonService.buildLibelle(declinaison);

  onProduitDeclinaisonChange = (produitDeclinaisonId: number): void => this.produitDeclinaisonId.set(produitDeclinaisonId);

  openSelection = (): void => {
    if (this.produitDeclinaisonId() == null) {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, `Choisissez d'abord un produit décliné`);
      return;
    }
    this.selectionVisible.set(true);
  };

  fermer = (): void => this.visible.set(false);

  openDialogPeriode = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.produitPeriode.set(produitComplementaire);
    this.dialogPeriodeVisible.set(true);
  };

  onPeriodeListChange = (periodeList: PeriodeConditionnementDTO[]): void => {
    const key = PlcProduitComplementaireChanges.buildKey(this.produitPeriode());
    this.propositionList.update(current => current.map(ligne => PlcProduitComplementaireChanges.buildKey(ligne) !== key
      ? ligne
      : {...ligne, periodeConditionnementList: periodeList}));
  };

  /**
   * Les lignes des autres produits déclinés sont conservées ; pour le produit décliné courant, la sélection fait foi
   * et les lignes déjà paramétrées sont réutilisées telles quelles.
   */
  onSelectionValidee = (selection: SelectionPlcProduitComplementaire): void => {
    const produitDeclinaisonId = this.produitDeclinaisonId();
    const plcByIdMap = new Map(selection.plcList.map(plc => [plc.plcId, plc]));

    this.propositionList.update(current => {
      const ligneByPlcIdMap = new Map(current.filter(ligne => ligne.produitDeclinaisonId === produitDeclinaisonId)
                                             .map(ligne => [ligne.plcId, ligne]));
      const ligneConserveeList = current.filter(ligne => ligne.produitDeclinaisonId !== produitDeclinaisonId);
      const ligneSelectionneeList = selection.plcIdList
                                             .filter(plcId => ligneByPlcIdMap.has(plcId) || plcByIdMap.has(plcId))
                                             .map(plcId => ligneByPlcIdMap.get(plcId) ?? this.buildLigne(plcByIdMap.get(plcId)));
      return [...ligneConserveeList, ...ligneSelectionneeList];
    });
  };

  /** Le back renvoie déjà une ligne prête à paramétrer : seuls les paramètres à saisir sont initialisés. */
  private buildLigne = (plc: PlcProduitComplementaireDTO): PlcProduitComplementaireDTO => ({
    ...plc,
    facturable: false,
    commandablePortail: false,
    jourConsommationList: [],
    periodeConditionnementList: []
  });

  retirerDeLaProposition = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    const key = PlcProduitComplementaireChanges.buildKey(produitComplementaire);
    this.propositionList.update(current => current.filter(ligne => PlcProduitComplementaireChanges.buildKey(ligne) !== key));
  };

  toggleJour = (jour: JourToggle): void => {
    const key = PlcProduitComplementaireChanges.buildKey(jour);
    this.propositionList.update(current => current.map(ligne => PlcProduitComplementaireChanges.buildKey(ligne) !== key
      ? ligne
      : this.paramSvc.toggleJourConsommationLigne(ligne, jour.jourId)));
  };

  ajouter = (): void => {
    this.grid()?.saveEditData().then(() => {
      const propositionList = this.propositionList();

      const errorMessage = this.paramSvc.buildParametrageIncompletMessage(propositionList);
      if (errorMessage) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, errorMessage);
        return;
      }

      this.produitComplementaireSvc.addProduits(propositionList)
          .subscribe(() => {
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS,
              `${propositionList.length} association(s) ajoutée(s) avec succès`);
            this.visible.set(false);
          });
    });
  };
}
